import { Flex, Modal, notification, Spin } from "antd";
import dayjs from "dayjs";
import QRCodeStyling from "qr-code-styling";
import { FC, useEffect, useRef, useState } from "react";
import { Coords } from "tm-shared/types";
import { createUniqueId, encodeJsonToBase64 } from "tm-shared/utils";

import { PrimaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";
import { getPreciseLocation } from "@/utils/geo/geo";

import {
    StyledContentBody,
    StyledInfoBlock,
    StyledModalTitle,
    StyledQRContainer,
} from "./Styled";

interface QRModalProps {
    open: boolean;
    onClose: () => void;
    marketId: string;
    marketName: string;
}

const base64Logo =
    "data:image/png;base64,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";

export const QrModal: FC<QRModalProps> = ({
    onClose,
    open,
    marketId,
    marketName,
}) => {
    const { lastName, firstName, roleName, id } = useAppSelector((s) => s.user);

    const [isLoading, setIsLoading] = useState(false);
    const [qrData, setQrData] = useState<string | null>(null);

    const qrRef = useRef<HTMLDivElement>(null);
    const qrCodeRef = useRef<QRCodeStyling | null>(null);

    const generateQR = async () => {
        setIsLoading(true);
        setQrData(null);

        let coords: Coords | null = null;

        try {
            coords = await getPreciseLocation();
        } catch (error) {
            if (error?.code === 1) {
                notification.warning({
                    message: "Доступ до геолокації не дозволено",
                });
            }
        }

        const data = {
            id: createUniqueId(),
            marketId,
            generatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            managerId: id,
            geo: coords,
        };

        setQrData(encodeJsonToBase64(data));
        setIsLoading(false);
    };

    useEffect(() => {
        if (open && !qrData && !isLoading) {
            generateQR();
        }
    }, [open]);

    useEffect(() => {
        if (!qrData || !qrRef.current) return;

        qrRef.current.innerHTML = "";

        qrCodeRef.current = new QRCodeStyling({
            width: 280,
            height: 280,
            type: "canvas",
            data: qrData,

            qrOptions: {
                errorCorrectionLevel: "H",
            },

            dotsOptions: {
                color: "#000000",
                type: "rounded",
            },

            backgroundOptions: {
                color: "#ffffff",
            },

            cornersSquareOptions: {
                color: "#3A57E8",
                type: "extra-rounded",
            },

            cornersDotOptions: {
                color: "#3A57E8",
                type: "dot",
            },

            image: base64Logo,
            imageOptions: {
                imageSize: 0.3,
                margin: 8,
            },
        });

        qrCodeRef.current.append(qrRef.current);
    }, [qrData]);

    const handleClose = () => {
        qrRef.current?.replaceChildren();
        qrCodeRef.current = null;
        setQrData(null);
        setIsLoading(false);
        onClose();
    };

    return (
        <Modal
            title={
                <StyledModalTitle>
                    <div className="title-main">QR-код для перевірки</div>
                    <div className="title-subtitle">{marketName}</div>
                </StyledModalTitle>
            }
            open={open}
            onCancel={handleClose}
            centered
            footer={null}
            maskClosable={false}
            width={500}
        >
            <StyledContentBody>
                {isLoading ? (
                    <Flex
                        vertical
                        align="center"
                        justify="center"
                        gap={24}
                        style={{ padding: "40px 20px" }}
                    >
                        <Spin size="large" />
                        <PrimaryText>Генерація QR-коду…</PrimaryText>
                    </Flex>
                ) : qrData ? (
                    <Flex
                        vertical
                        align="center"
                        justify="center"
                        gap={20}
                        style={{ padding: "20px" }}
                    >
                        <StyledQRContainer>
                            <div ref={qrRef} />
                        </StyledQRContainer>

                        <StyledInfoBlock>
                            <div className="info-item">
                                <span className="info-label">
                                    Співробітник:
                                </span>
                                <span className="info-value">
                                    {lastName} {firstName}
                                </span>
                            </div>

                            <div className="info-item">
                                <span className="info-label">Роль:</span>
                                <span className="info-value">{roleName}</span>
                            </div>

                            <div className="info-item">
                                <span className="info-label">
                                    Час створення:
                                </span>
                                <span className="info-value">
                                    {dayjs().format("DD.MM.YYYY HH:mm")}
                                </span>
                            </div>
                        </StyledInfoBlock>
                    </Flex>
                ) : null}
            </StyledContentBody>
        </Modal>
    );
};
